import {
  Box,
  Center,
  HStack,
  Icon,
  StackProps,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import {
  entries,
  find,
  has,
  includes,
  isBoolean,
  isEmpty,
  sortBy,
  startsWith,
  toLower,
} from 'lodash'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import { FaDownload, FaTable, FaTimes } from 'react-icons/fa'
import { Button } from '../Button'
import { FieldValue, TableRecord } from '../Table/types'
import ColumnHeader from './ColumnHeader'
import { exportGridTableToCsv } from './exportGridTableToCsv'
import HeadingToolbar from './HeadingToolbar'
import IncludedColumnsModal from './IncludedColumnsModal'
import { InputFilter } from './InputFilter'
import PaginationToolbar from './PaginationToolbar'
import { SelectFilter } from './SelectFilter'
import { GridTableColumn, GridTableRecord, PaginationProps } from './types'

interface GridTableProps extends StackProps {
  title?: string
  columns: GridTableColumn[]
  records: GridTableRecord[]
  paginationProps?: PaginationProps
  height: number
  width: number
}

const defaultCellRenderer = ({ value }: { value: any }) => <span>{value}</span>

enum FilterType {
  Input,
  Select,
}

interface GridTableFilter {
  recordField: string
  value: string
  filterType: FilterType
}

const PADDING_BUFFER = 20

const GridTable = ({
  title,
  columns,
  records,
  paginationProps,
  height,
  width,
  ...baseProps
}: GridTableProps) => {
  const getInitiallyIncludedColumns = () =>
    columns
      .filter(column => column.isVisibleByDefault)
      .map(column => column.recordField)
  const [sortByRecordField, setSortByRecordField] = useState<{
    [index: string]: boolean
  }>({ recordField: false })
  const [filterRecordsBy, setFilterRecordsBy] = useState<GridTableFilter[]>([])
  const [filteredRecords, setFilteredRecords] = useState(records)
  const [pageIndex, setPageIndex] = useState(0)
  const [includedColumns, setIncludedColumns] = useState(
    getInitiallyIncludedColumns()
  )
  const [recordsPerPage, setRecordsPerPage] = useState(
    paginationProps && !isEmpty(paginationProps.recordsPerPage)
      ? paginationProps.recordsPerPage[0]
      : 7
  )
  const {
    isOpen: isColumnsModalOpen,
    onOpen: onColumnsModalOpen,
    onClose: onColumnsModalClose,
  } = useDisclosure()
  const headingToolbarRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const tbodyRef = useRef<HTMLTableSectionElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const hasFilters = columns.some(column => column.isFilterable)
  const numberOfPages = paginationProps
    ? Math.max(Math.ceil(filteredRecords.length / recordsPerPage), 1)
    : 1
  const visibleColumns = columns.filter(({ recordField }) =>
    includes(includedColumns, recordField)
  )

  let displayedRecords = filteredRecords
  if (paginationProps) {
    const startingRecordIndex = pageIndex * recordsPerPage
    const endingRecordIndex = startingRecordIndex + recordsPerPage
    displayedRecords = filteredRecords.slice(
      startingRecordIndex,
      endingRecordIndex
    )
  }

  useEffect(() => {
    let nextFilteredRecords = records
    filterRecordsBy.forEach(
      ({ filterType, recordField, value: filterValue }: GridTableFilter) => {
        if (filterType === FilterType.Select) {
          nextFilteredRecords = nextFilteredRecords.filter(
            record => record[recordField] === filterValue
          )
        } else {
          nextFilteredRecords = nextFilteredRecords.filter(record =>
            startsWith(toLower(`${record[recordField]}`), toLower(filterValue))
          )
        }
      }
    )
    setFilteredRecords(nextFilteredRecords)
    setPageIndex(0)
  }, [filterRecordsBy])

  useEffect(() => {
    const [[recordField, ascending]] = entries(sortByRecordField)
    setFilteredRecords(previousFilteredRecords => {
      const sortedFilteredRecords = sortBy(
        previousFilteredRecords,
        record => record[recordField]
      )
      return ascending ? sortedFilteredRecords : sortedFilteredRecords.reverse()
    })
    setPageIndex(0)
  }, [sortByRecordField])

  const onFilterChange = (filter: GridTableFilter) =>
    setFilterRecordsBy(previousFilterRecordsBy => {
      const otherFilters = previousFilterRecordsBy.filter(
        previousFilter => previousFilter.recordField !== filter.recordField
      )
      return isEmpty(filter.value) ? otherFilters : otherFilters.concat(filter)
    })

  const handleColumnHeaderClick = (recordField: string) =>
    setSortByRecordField(previousSortByRecordField => {
      const sortingByRecordFieldAlready = has(
        previousSortByRecordField,
        recordField
      )
      const sortAscending = sortingByRecordFieldAlready
        ? !previousSortByRecordField[recordField]
        : false
      return {
        [recordField]: sortAscending,
      }
    })

  const getFilterValue = (column: GridTableColumn) => {
    const filter = find(filterRecordsBy, { recordField: column.recordField })
    return (filter && filter.value) || ''
  }

  const getFilteredOptions = (column, filteredRecords) =>
    column.options.map(option => ({
      ...option,
      disabled:
        find(filteredRecords, {
          [column.recordField]: option.value,
        }) === undefined,
    }))

  const getElementHeight = (elementRef: RefObject<HTMLElement>) =>
    (elementRef && elementRef.current && elementRef.current.offsetHeight) || 0

  const bordersOffset = 1
  const tableColumnsWidth = visibleColumns.reduce(
    (accumulator, currentColumn) =>
      accumulator + (currentColumn.width + bordersOffset || 0),
    0
  )
  const tableDisplayWidth = Math.min(
    width || tableColumnsWidth,
    tableColumnsWidth
  )
  const displayWidth = tableDisplayWidth + bordersOffset * 2
  const tableBodyHeight =
    height -
    getElementHeight(headingToolbarRef) -
    getElementHeight(theadRef) -
    getElementHeight(footerRef)

  return (
    <VStack {...baseProps} spacing={0}>
      <HeadingToolbar
        ref={headingToolbarRef}
        title={title}
        width={displayWidth}
      >
        <Button
          border="0"
          textColor="white"
          variant="link"
          onClick={() => setFilterRecordsBy([])}
        >
          <Icon as={FaTimes} mr={1} />
          Clear Filters
        </Button>
        <Button
          border="0"
          textColor="white"
          variant="link"
          onClick={() => exportGridTableToCsv(visibleColumns, filteredRecords)}
        >
          <Icon as={FaDownload} mr={1} />
          Export to CSV
        </Button>
        <Button
          border="0"
          textColor="white"
          variant="link"
          onClick={onColumnsModalOpen}
        >
          <Icon as={FaTable} />
          <IncludedColumnsModal
            allColumns={columns}
            includedColumns={includedColumns}
            isOpen={isColumnsModalOpen}
            onClose={onColumnsModalClose}
            onColumnChange={(recordField, includeColumn) =>
              setIncludedColumns(previouslyIncludedColumns =>
                includeColumn
                  ? previouslyIncludedColumns.concat(recordField)
                  : previouslyIncludedColumns.filter(
                      column => column !== recordField
                    )
              )
            }
            onReset={() => setIncludedColumns(getInitiallyIncludedColumns())}
          />
        </Button>
      </HeadingToolbar>
      <Box borderLeftWidth={1} borderRightWidth={1} borderColor="gray.500">
        <Table
          width={tableDisplayWidth}
          sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}
          role="grid"
        >
          <Thead
            display="block"
            overflowX="auto"
            borderLeftWidth={0}
            borderRightWidth={0}
            ref={theadRef}
            // @ts-ignore
            onScroll={(e: UIEvent<HTMLTableSectionElement, UIEvent>) => {
              if (tbodyRef && tbodyRef.current) {
                tbodyRef.current.scrollLeft = e.target.scrollLeft
              }
            }}
          >
            <Tr>
              {visibleColumns.map(
                ({
                  key,
                  label,
                  recordField,
                  width,
                  isNumeric = false,
                  isSortable = true,
                  isCentered = false,
                }: GridTableColumn) => {
                  const sortAscending = sortByRecordField[recordField]
                  const sortingByColumn = isBoolean(sortAscending)
                  return (
                    <ColumnHeader
                      key={key}
                      label={label}
                      isSortable={isSortable}
                      onClick={() => handleColumnHeaderClick(recordField)}
                      isSortingBy={sortingByColumn}
                      sortAscending={sortAscending}
                      width={width}
                      isNumeric={isNumeric}
                      isCentered={isCentered}
                    />
                  )
                }
              )}
            </Tr>
            {hasFilters && (
              <Tr className="filter-row">
                {visibleColumns.map((column: GridTableColumn) => (
                  <Th
                    key={column.key}
                    bg="gray.100"
                    borderTopColor="gray.500"
                    borderTopWidth={1}
                    borderBottomColor="gray.500"
                    minWidth={column.width}
                    width={column.width}
                    maxWidth={column.width}
                  >
                    {column.options ? (
                      <SelectFilter
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          onFilterChange({
                            recordField: column.recordField,
                            value: e.target.value,
                            filterType: FilterType.Select,
                          })
                        }
                        options={getFilteredOptions(column, filteredRecords)}
                        value={getFilterValue(column)}
                        width={column.width - PADDING_BUFFER}
                      />
                    ) : (
                      <InputFilter
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onFilterChange({
                            recordField: column.recordField,
                            value: e.target.value,
                            filterType: FilterType.Input,
                          })
                        }
                        value={getFilterValue(column)}
                        width={column.width - PADDING_BUFFER}
                      />
                    )}
                  </Th>
                ))}
              </Tr>
            )}
          </Thead>
          <Tbody
            ref={tbodyRef}
            overflow="scroll"
            display="block"
            height={tableBodyHeight}
            // @ts-ignore
            onScroll={(e: UIEvent<HTMLTableSectionElement, UIEvent>) => {
              if (theadRef && theadRef.current) {
                theadRef.current.scrollLeft = e.target.scrollLeft
              }
            }}
          >
            {displayedRecords.map((record: TableRecord) => (
              <Tr key={record.key}>
                {entries(record).map(([key, value]: [string, FieldValue]) => {
                  const colData = find(
                    visibleColumns,
                    c => c.recordField === key
                  )!
                  const Cell = colData?.cellRenderer || defaultCellRenderer
                  return (
                    colData &&
                    key !== 'key' && (
                      <Td
                        key={colData?.key}
                        minWidth={colData.width}
                        width={colData.width}
                        maxWidth={colData.width}
                        isNumeric={colData.isNumeric}
                      >
                        {colData.isCentered ? (
                          <Center>
                            <Cell value={value} />
                          </Center>
                        ) : (
                          <Cell value={value} />
                        )}
                      </Td>
                    )
                  )
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <HStack
        ref={footerRef}
        spacing="2rem"
        borderWidth={1}
        borderColor="gray.500"
        bg="gray.100"
        paddingLeft="1rem"
        paddingRight="1rem"
        width={displayWidth}
      >
        <span>{`${filteredRecords.length.toLocaleString()} ${
          filteredRecords.length === 1 ? 'record' : 'records'
        }`}</span>
        {paginationProps && (
          <PaginationToolbar
            spacing="2rem"
            numberOfPages={numberOfPages}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            recordsPerPage={paginationProps.recordsPerPage}
            onRecordsPerPageChange={newRecordsPerPage => {
              setPageIndex(0)
              setRecordsPerPage(newRecordsPerPage)
            }}
          />
        )}
      </HStack>
    </VStack>
  )
}

export default GridTable
